import { IPage, Loading } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import { IGroupAccessOwnerColumn } from '../../../models/GroupAccessOwners/GroupAccessOwnerColumn';
import { IGroupAccessOwnerRowData } from '../../../models/GroupAccessOwners/GroupAccessOwnerRow';
import { IPerson } from '../../../models/GroupAccessOwners/PersonVm';
import { IQueryOptions } from '../../../models/GroupAccessOwners/ServiceInterfaces';
import { TFetchableEntity } from '../../../models/IFetchableEntity';
import { IBaseViewScope } from '../../../store/GroupAccessOwners/ViewStore';
import { NameOf } from '../../../utilities/GroupAccessOwners/CommonUtil';
import { GenerateTableColumns, GenerateRowRenderTemplate } from '../../../utilities/GroupAccessOwners/GroupAccesUtil/View';
import { Container } from './Container';
import { IFilterable, IQueryableTableViewProps, QueryableTableView } from './QueryableTableView';

export interface IBaseHierarchyViewScope extends IBaseViewScope {
	forHierarchy: string;
}

export function IsScopedToHierarchy(scope: IBaseViewScope): scope is IBaseHierarchyViewScope {
	return NameOf<IBaseHierarchyViewScope>('forHierarchy') in scope;
}

export interface IGroupAccessOwnerListBaseProps<TScope extends IBaseViewScope> {
	columns: TFetchableEntity<IGroupAccessOwnerColumn[]>;
	filterables: TFetchableEntity<IFilterable[]>;
	people: TFetchableEntity<IPage<IPerson>>;

	scope: TScope;
	constructColumns: (scope: TScope, requestedByView: string) => void;
	constructFilterables: (scope: TScope, requestedByView: string) => void;
	queryForOwners: (query: IQueryOptions, scope: TScope, requestedByView: string) => void;

	renderExpandedRowContent?: (rowData: IGroupAccessOwnerRowData) => any;
	renderList: IQueryableTableViewProps<IGroupAccessOwnerRowData>['renderView'];

	viewIdentifier: string;

	whenDataIsNotAvailable?: Partial<{
		render: () => JSX.Element;
		handle: () => void;
	}>;
}

export class BaseOwnerListView<T extends IBaseViewScope> extends React.Component<IGroupAccessOwnerListBaseProps<T>> {
	public componentDidMount() {
		// Configure columns + filterables on initial mount
		const { constructColumns, constructFilterables, scope, viewIdentifier } = this.props;

		constructColumns(scope, viewIdentifier);
		constructFilterables(scope, viewIdentifier);
	}

	public componentDidUpdate(prevProps: IGroupAccessOwnerListBaseProps<T>) {
		const dataNotAvailable = (props: IGroupAccessOwnerListBaseProps<T>) => {
			const { columns, filterables, people } = props;
			return [columns, filterables, people].some((dep) => !dep.isLoading && dep.couldNotFetchData);
		};

		if (!dataNotAvailable(prevProps) && dataNotAvailable(this.props)) {
			const { whenDataIsNotAvailable } = this.props;

			// Invoke callback
			whenDataIsNotAvailable?.handle?.();
		}
	}

	public render() {
		const viewProps = this.GenerateViewProps();

		if (viewProps === 'Loading') {
			return (
				<Container aligned={'center'}>
					<Loading height={'70px'} width={'70px'} />
				</Container>
			);
		}

		if (viewProps === 'Data N/A') {
			const { whenDataIsNotAvailable } = this.props;

			// Render content when data is N/A and the table view cannot be rendered.
			return whenDataIsNotAvailable?.render?.() ?? null;
		}

		return <QueryableTableView {...viewProps} />;
	}

	private GenerateViewProps(): IQueryableTableViewProps<IGroupAccessOwnerRowData> | 'Loading' | 'Data N/A' {
		const { columns, filterables, people, queryForOwners, renderExpandedRowContent, renderList, scope } = this.props;

		// If we are awaiting on columns or filterables
		if (columns.isLoading || filterables.isLoading) {
			return 'Loading';
		}

		// If data is N/A
		if (columns.couldNotFetchData || filterables.couldNotFetchData || (!people.isLoading && people.couldNotFetchData)) {
			return 'Data N/A';
		}

		return {
			columns: GenerateTableColumns(columns),
			filterables,

			rowData: !people.isLoading ? this.ComposePagedRowData(people) : 'Loading',
			rowRenderTemplate: GenerateRowRenderTemplate(columns, renderExpandedRowContent),
			requestData: (query) => queryForOwners(query, scope, this.props.viewIdentifier),

			renderView: renderList,
		};
	}

	private ComposePagedRowData(forPeople: IPage<IPerson>): IPage<IGroupAccessOwnerRowData> {
		const { scope } = this.props;

		return {
			pageRange: forPeople.pageRange,
			items: forPeople.items.map((person) => ({
				owner: person,
				hierarchy: IsScopedToHierarchy(scope) ? scope.forHierarchy : undefined,
			})),
		};
	}
}
